import { Check } from 'lucide-react'
import type { ComponentType } from 'react'
import { Card, CardContent } from '@/components/ui/card'

import { BILLING_CYCLES, CREATIVE_TIERS } from '../config'
import { cn } from '@/lib/utils'

type BillingCycleKey = keyof typeof BILLING_CYCLES
type CreativeTierKey = keyof typeof CREATIVE_TIERS
type PlanId = 'license' | 'support' | 'creative'

const BILLING_CYCLE_KEYS = Object.keys(BILLING_CYCLES) as BillingCycleKey[]
const CREATIVE_TIER_KEYS = Object.keys(CREATIVE_TIERS) as CreativeTierKey[]

interface SubscriptionPlan {
    id: PlanId
    title: string
    Icon: ComponentType<{ size?: number; color?: string }>
    iconColor: string
    iconBg: string
    kind: 'cycle' | 'tier'
    features: string[]
}

const PILL_BASE =
    'tw:!inline-flex tw:!flex-1 tw:!items-center tw:!justify-center tw:!px-[14px] tw:!py-[6px] tw:!rounded-[20px] tw:!border tw:!text-[12px] tw:!font-semibold tw:!leading-none tw:!whitespace-nowrap tw:!transition-colors tw:!shadow-none tw:!bg-transparent'
const PILL_ACTIVE_BLUE = 'tw:!bg-[rgba(59,108,245,0.15)] tw:!border-[#3B6CF5] tw:!text-[#3B6CF5]'
const PILL_ACTIVE_PURPLE = 'tw:!bg-[rgba(48,29,87,0.15)] tw:!border-[#9F7AEA] tw:!text-[#9F7AEA]'
const PILL_INACTIVE = 'tw:!border-[#1E2640] tw:!text-[#7A8BA8] hover:tw:!text-white hover:tw:!border-[#35619A]'

const CARD_BACKGROUND_STYLE = {
    background:
        'linear-gradient(#0F1F31, #0F1F31) padding-box, linear-gradient(180deg, rgba(43,59,76,1) 0%, rgba(15,31,49,1) 100%) border-box',
    border: '1px solid transparent',
    boxShadow: '0px 40px 40px 0px rgba(3, 13, 25, 0.12)',
}

const priceFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
})

interface PlanCardProps {
    plan: SubscriptionPlan
    cycle: BillingCycleKey | undefined
    tier: CreativeTierKey
    isSubscribed: boolean
    onCycleChange: (planId: PlanId, cycle: BillingCycleKey) => void
    onTierChange: (tier: CreativeTierKey) => void
    onSubscribe: (planId: PlanId) => void
    pending?: boolean
    subscribedLabel?: string
    idleLabel?: string
    disabled?: boolean
    amount?: number | null
    disableWhenSubscribed?: boolean
    /** When set, replaces `plan.features` in the list */
    featureLines?: string[]
    /** Creative card: billing frequency (second axis) */
    creativeFrequency?: BillingCycleKey
    onCreativeFrequencyChange?: (cycle: BillingCycleKey) => void
    /** Creative card: ads count for the selected tier + frequency (from catalog plan) */
    selectedCreativesQuantity?: number | null
}

const PlanCard = ({
    plan,
    cycle,
    tier,
    isSubscribed,
    onCycleChange,
    onTierChange,
    onSubscribe,
    pending = false,
    subscribedLabel = 'Subscribed',
    idleLabel = 'Subscribe',
    disabled = false,
    amount,
    disableWhenSubscribed = true,
    featureLines,
    creativeFrequency,
    onCreativeFrequencyChange,
    selectedCreativesQuantity,
}: PlanCardProps) => {
    const Icon = plan.Icon
    const isCreativesCard =
        plan.kind === 'tier' && typeof onCreativeFrequencyChange === 'function' && creativeFrequency !== undefined

    const hasAmount = typeof amount === 'number' && Number.isFinite(amount)

    const cycleForSubtitle = isCreativesCard ? (creativeFrequency ?? 'monthly') : (cycle ?? 'monthly')
    const durationLine = BILLING_CYCLES[cycleForSubtitle].duration

    const featuresToShow = featureLines ?? plan.features
    const adsLine =
        isCreativesCard && typeof selectedCreativesQuantity === 'number'
            ? `${selectedCreativesQuantity} ads/month`
            : null

    return (
        <Card
            className="tw:flex tw:min-h-0 tw:w-full tw:!gap-0 tw:!py-0 tw:max-[499px]:h-auto tw:min-[500px]:h-[600px] tw:min-[500px]:w-[280px] tw:min-[500px]:shrink-0 tw:!border-0 tw:rounded-[4px] tw:shadow-none tw:overflow-hidden"
            style={CARD_BACKGROUND_STYLE}
        >
            <CardContent className="tw:!bg-transparent tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-6 tw:overflow-y-auto tw:rounded-[4px] tw:p-6">
                <div
                    className={cn(
                        'tw:flex tw:flex-col tw:items-center tw:justify-start tw:text-center tw:gap-[11px] tw:shrink-0',
                        'tw:max-[499px]:h-auto tw:max-[499px]:min-h-0',
                        'tw:min-[500px]:h-[400px] tw:min-[500px]:flex-none',
                    )}
                >
                    <div
                        className="tw:w-10 tw:h-10 tw:rounded-full tw:flex tw:items-center tw:justify-center"
                        style={{ backgroundColor: plan.iconBg }}
                    >
                        <Icon size={24} color={plan.iconColor} />
                    </div>
                    <div className="tw:flex tw:flex-col tw:gap-[3px] tw:w-full">
                        <h3 className="tw:text-[18px] tw:font-semibold tw:leading-[1.4] tw:text-white">{plan.title}</h3>
                        <div className="tw:flex tw:justify-center tw:items-end tw:gap-1">
                            <span className="tw:text-[38px] tw:font-bold tw:leading-none tw:text-white">
                                {hasAmount ? priceFormatter.format(Number(amount)) : 'N/A'}
                            </span>
                            <span className="tw:text-[16px] tw:font-normal tw:leading-[1.5] tw:text-[#899CBD]">
                                {durationLine}
                            </span>
                        </div>
                    </div>

                    {isCreativesCard ? (
                        <div className="tw:flex tw:flex-col tw:w-full tw:gap-1.5 tw:mt-1">
                            <div className="tw:flex tw:w-full tw:gap-1.5">
                                {BILLING_CYCLE_KEYS.map((value) => (
                                    <button
                                        key={value}
                                        type="button"
                                        aria-pressed={creativeFrequency === value}
                                        onClick={() => onCreativeFrequencyChange(value)}
                                        className={cn(
                                            PILL_BASE,
                                            creativeFrequency === value ? PILL_ACTIVE_BLUE : PILL_INACTIVE,
                                        )}
                                    >
                                        {BILLING_CYCLES[value].label}
                                    </button>
                                ))}
                            </div>
                            <div className="tw:flex tw:w-full tw:gap-1.5">
                                {CREATIVE_TIER_KEYS.map((value) => (
                                    <button
                                        key={value}
                                        type="button"
                                        aria-pressed={tier === value}
                                        onClick={() => onTierChange(value)}
                                        className={cn(PILL_BASE, tier === value ? PILL_ACTIVE_PURPLE : PILL_INACTIVE)}
                                    >
                                        {CREATIVE_TIERS[value].label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="tw:flex tw:w-full tw:gap-1.5 tw:mt-1">
                            {BILLING_CYCLE_KEYS.map((value) => (
                                <button
                                    key={value}
                                    type="button"
                                    aria-pressed={cycle === value}
                                    onClick={() => onCycleChange(plan.id, value)}
                                    className={cn(PILL_BASE, cycle === value ? PILL_ACTIVE_BLUE : PILL_INACTIVE)}
                                >
                                    {BILLING_CYCLES[value].label}
                                </button>
                            ))}
                        </div>
                    )}

                    {adsLine && (
                        <p className="tw:text-[16px] tw:font-normal tw:leading-[1.5] tw:text-[#899CBD]">{adsLine}</p>
                    )}
                </div>

                <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:justify-end tw:gap-1.5">
                    <p className="tw:shrink-0 tw:text-[14px] tw:font-normal tw:leading-[1.5] tw:text-white tw:whitespace-nowrap">
                        Key Features:
                    </p>
                    <ul
                        className="tw:flex tw:flex-col tw:gap-0 tw:!pl-0 tw:!ml-0 tw:list-none"
                        style={{ paddingInlineStart: 0, marginInlineStart: 0 }}
                    >
                        {featuresToShow.map((feature, idx) => (
                            <li
                                key={`${plan.id}-${idx}-${feature}`}
                                className="tw:flex tw:items-center tw:gap-1 tw:text-[13px] tw:font-normal tw:text-[#899CBD] tw:leading-[1.5] tw:!pl-0 tw:!ml-0"
                                style={{ paddingInlineStart: 0, marginInlineStart: 0 }}
                            >
                                <Check className="tw:w-6 tw:h-6 tw:text-[#3B6CF5] tw:shrink-0" strokeWidth={2.5} />
                                <span>{feature}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <button
                    type="button"
                    className={cn(
                        'tw:w-full tw:h-9 tw:min-h-9 tw:max-h-9 tw:shrink-0 tw:px-3 tw:gap-1 tw:text-[14px] tw:font-medium tw:leading-none tw:whitespace-nowrap tw:rounded-[2px] tw:inline-flex tw:items-center tw:justify-center tw:disabled:opacity-60 tw:disabled:cursor-not-allowed',
                        isSubscribed
                            ? 'tw:bg-[#1A3672] hover:tw:bg-[#23488F] tw:text-white'
                            : 'tw:bg-[#2450BF] hover:tw:bg-[#2C63E8] tw:text-white',
                    )}
                    onClick={() => onSubscribe(plan.id)}
                    disabled={disabled || pending || (disableWhenSubscribed && isSubscribed)}
                >
                    {pending ? 'Processing…' : isSubscribed ? subscribedLabel : idleLabel}
                </button>
            </CardContent>
        </Card>
    )
}

export default PlanCard
